import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../header/header.component';
import { SubheaderComponent } from '../subheader/subheader.component';
import { ParagraphComponent } from '../paragraph/paragraph.component';
import { ListComponent } from '../list/list.component';
import { TableComponent } from '../table/table.component';
import { KeyValueComponent } from '../key-value/key-value.component';
import { PromptUserComponent } from '../prompt-user/prompt-user.component';

export type ComponentType =
  | 'header'
  | 'subheader'
  | 'paragraph'
  | 'list'
  | 'table'
  | 'key_value'
  | 'user_question';

export interface ComponentData {
  type: ComponentType;
  content: any;
}

@Component({
  selector: 'app-message-renderer',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    SubheaderComponent,
    ParagraphComponent,
    ListComponent,
    TableComponent,
    KeyValueComponent,
    PromptUserComponent,
  ],
  templateUrl: './message-renderer.component.html',
  styleUrl: './message-renderer.component.scss',
})
export class MessageRendererComponent {
  @Input() components: ComponentData[] = [];
  @Input() disabled = false;
  @Output() promptSend = new EventEmitter<string>();

  onPromptSend(value: string): void {
    this.promptSend.emit(value);
  }

  // Static method to parse JSON string or object into ComponentData[]
  static parseMessage(message: string | object): ComponentData[] {
    try {
      let parsed: any;

      if (typeof message === 'string') {
        parsed = JSON.parse(message);
      } else {
        parsed = message;
      }

      // If it's already an array of components
      if (Array.isArray(parsed)) {
        return parsed.map((comp) => ({
          type: comp.type as ComponentType,
          content: comp.content,
        }));
      }

      // If it's a single component object
      if (parsed && parsed.type && parsed.content) {
        return [
          {
            type: parsed.type as ComponentType,
            content: parsed.content,
          },
        ];
      }

      // If it's a plain object, try to infer the type
      if (parsed && typeof parsed === 'object') {
        // Check for table structure
        if (parsed.columns && Array.isArray(parsed.columns) && parsed.rows) {
          return [
            {
              type: 'table',
              content: parsed,
            },
          ];
        }
        // Check for list structure
        if (parsed.items && Array.isArray(parsed.items)) {
          return [
            {
              type: 'list',
              content: {
                ordered: parsed.ordered || false,
                items: parsed.items,
              },
            },
          ];
        }
        // Check for text with level (subheader)
        if (parsed.text && parsed.level !== undefined) {
          return [
            {
              type: 'subheader',
              content: {
                text: parsed.text,
                level: parsed.level || 2,
              },
            },
          ];
        }
        // Check for plain text (header or paragraph)
        if (parsed.text) {
          return [
            {
              type: 'header',
              content: {
                text: parsed.text,
              },
            },
          ];
        }
      }

      // Fallback: treat as plain text paragraph
      return [
        {
          type: 'paragraph',
          content: {
            text: typeof message === 'string' ? message : JSON.stringify(message),
          },
        },
      ];
    } catch (error) {
      // If parsing fails, treat as plain text
      return [
        {
          type: 'paragraph',
          content: {
            text: typeof message === 'string' ? message : JSON.stringify(message),
          },
        },
      ];
    }
  }
}

